import type { ClientResource } from '@voxler/contracts';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { internalFetch } from '../lib/api';
import { percentUsed } from '../components/client-helpers';
import { currentMonth, formatEurCents } from '@/lib/format';
import { PageChrome } from '@/components/page-chrome';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Card, CardBar, CardContent, CardHead } from '@/components/ui/card';
import { Meter } from '@/components/ui/chart';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Row4, Split21 } from '@/components/ui/layout-grid';
import { PageHead, formatMonthLabel } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs, type TableTabItem } from '@/components/ui/tabs';

type AlertScope = 'client' | 'platform';
type AlertStatus = 'pending' | 'sent' | 'failed';

interface Alert {
  id: string;
  scope: AlertScope;
  clientId: string | null;
  type: string;
  threshold: number | null;
  month: string;
  message: string;
  status: AlertStatus;
  sentAt: string | null;
  createdAt: string;
}

interface ClientUsageResponse {
  month: string;
  spent: string;
  providerCost: string;
}

type Filter = 'all' | AlertStatus;

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'sent', label: 'Envoyées' },
  { key: 'failed', label: 'Échec' },
];

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const PERCENT_FORMAT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** Formats an ISO timestamp into a short French date + time, or a dash when absent/invalid. */
function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return DATE_TIME_FORMAT.format(date);
}

function scopeLabel(scope: AlertScope): string {
  switch (scope) {
    case 'client':
      return 'Client';
    case 'platform':
      return 'Plateforme';
  }
}

/** French label of an alert type; unknown types fall back to their technical key. */
function alertTypeLabel(type: string): string {
  switch (type) {
    case 'safety_threshold':
      return 'Seuil de sécurité';
    case 'platform_threshold':
      return 'Seuil plateforme';
    case 'client_paused':
      return 'Client en pause';
    case 'platform_paused':
      return 'Plateforme en pause';
    case 'payment_failed':
      return 'Paiement échoué';
    default:
      return type;
  }
}

interface StatusView {
  variant: BadgeVariant;
  label: string;
}

function statusView(status: AlertStatus): StatusView {
  switch (status) {
    case 'sent':
      return { variant: 'success', label: 'Envoyée' };
    case 'pending':
      return { variant: 'info', label: 'En attente' };
    case 'failed':
      return { variant: 'danger', label: 'Échec' };
  }
}

function emptyView(filter: Filter): { title: string; description: string } {
  switch (filter) {
    case 'all':
      return {
        title: 'Aucune alerte pour le moment',
        description: 'Les alertes déclenchées par les seuils et les pauses apparaissent ici.',
      };
    case 'pending':
      return {
        title: 'Aucune alerte en attente',
        description: 'Les alertes déclenchées apparaissent ici jusqu’à leur envoi.',
      };
    case 'sent':
      return {
        title: 'Aucune alerte envoyée',
        description: 'Une alerte envoyée apparaît ici avec sa date d’envoi.',
      };
    case 'failed':
      return {
        title: 'Aucune alerte en échec',
        description: 'Un échec d’envoi apparaîtra ici.',
      };
  }
}

/** Badge of a consumption line: highest alert threshold crossed, or within thresholds. */
function thresholdBadge(pct: number, thresholds: readonly number[]): StatusView {
  if (pct >= 100) {
    return { variant: 'danger', label: 'Dépassé' };
  }
  const crossed = thresholds.filter((threshold) => pct >= threshold);
  if (crossed.length > 0) {
    return { variant: 'warning', label: `Seuil à ${Math.max(...crossed)}\u00a0%` };
  }
  return { variant: 'success', label: 'Dans le seuil' };
}

function ThresholdRow({
  client,
  usage,
}: {
  client: ClientResource;
  usage: ClientUsageResponse | undefined;
}): JSX.Element {
  const pct = usage === undefined ? null : percentUsed(usage.spent, client.safetyCap);
  const below100 = client.alertThresholds.filter((threshold) => threshold < 100);
  const marker = below100.length > 0 ? Math.min(...below100) : null;
  const badge = pct === null ? null : thresholdBadge(pct, client.alertThresholds);
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-small font-medium">{client.name}</span>
        {badge !== null ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
      </div>
      <Meter
        percent={pct ?? 0}
        threshold={marker}
        ariaLabel={
          pct === null
            ? `${client.name} : consommation indisponible`
            : `${client.name} : ${PERCENT_FORMAT.format(pct)}\u00a0% du plafond`
        }
      />
      <div className="flex justify-between gap-2 font-mono text-xs text-muted-foreground">
        <span>
          {usage === undefined
            ? 'consommation…'
            : `${formatEurCents(usage.spent)} sur ${formatEurCents(client.safetyCap)} HT`}
        </span>
        <span>{pct === null ? '—' : `${PERCENT_FORMAT.format(pct)}\u00a0%`}</span>
      </div>
    </div>
  );
}

export function AlertsPage(): JSX.Element {
  const [filter, setFilter] = useState<Filter>('all');

  const alerts = useQuery({
    queryKey: ['alerts'],
    queryFn: () => internalFetch<{ alerts: Alert[] }>('/internal/alerts'),
  });
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });
  const clientList = useMemo(() => clients.data?.clients ?? [], [clients.data]);
  const usages = useQueries({
    queries: clientList.map((client) => ({
      queryKey: ['client-usage', client.id],
      queryFn: () => internalFetch<ClientUsageResponse>(`/internal/clients/${client.id}/usage`),
    })),
  });

  const clientNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clientList) {
      map.set(client.id, client.name);
    }
    return map;
  }, [clientList]);

  const all = useMemo(
    () => [...(alerts.data?.alerts ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [alerts.data],
  );

  const count = (key: Filter): number =>
    key === 'all' ? all.length : all.filter((alert) => alert.status === key).length;
  const rows = useMemo(
    () => all.filter((alert) => filter === 'all' || alert.status === filter),
    [all, filter],
  );
  const pagination = usePagination(rows, 25);

  const clientLabel = (alert: Alert): string => {
    if (alert.scope === 'platform' || alert.clientId === null) {
      return '—';
    }
    return clientNames.get(alert.clientId) ?? alert.clientId;
  };

  const month = currentMonth();
  const monthStats = useMemo(() => {
    const ofMonth = all.filter((alert) => alert.month === month);
    const thresholdAlerts = ofMonth.filter((alert) => alert.threshold !== null);
    const thresholdClients = [
      ...new Set(
        thresholdAlerts.map((alert) =>
          alert.scope === 'platform' || alert.clientId === null
            ? 'Plateforme'
            : (clientNames.get(alert.clientId) ?? alert.clientId),
        ),
      ),
    ];
    return {
      total: ofMonth.length,
      sent: ofMonth.filter((alert) => alert.status === 'sent').length,
      pending: ofMonth.filter((alert) => alert.status === 'pending').length,
      failed: ofMonth.filter((alert) => alert.status === 'failed').length,
      thresholds: thresholdAlerts.length,
      thresholdClients,
    };
  }, [all, month, clientNames]);

  const tabs: TableTabItem<Filter>[] = FILTERS.map((item) => ({
    value: item.key,
    label: item.label,
    count: count(item.key),
  }));
  const empty = emptyView(filter);

  const thresholdLines = clientList
    .map((client, index) => ({ client, usage: usages[index]?.data }))
    .sort((a, b) => {
      const pa =
        a.usage === undefined ? -1 : (percentUsed(a.usage.spent, a.client.safetyCap) ?? -1);
      const pb =
        b.usage === undefined ? -1 : (percentUsed(b.usage.spent, b.client.safetyCap) ?? -1);
      return pb - pa;
    });

  return (
    <>
      <PageChrome crumbs={[{ label: 'Alertes' }]} />
      <PageHead title="Alertes" subtitle="Historique et statut d'envoi" />

      {alerts.isSuccess ? (
        <Row4 aria-label="Indicateurs des alertes">
          <StatCard
            label="Alertes du mois"
            value={String(monthStats.total)}
            foot={formatMonthLabel(month)}
          />
          <StatCard
            label="Envoyées"
            value={String(monthStats.sent)}
            foot={`${monthStats.pending} en attente, ${monthStats.failed} en échec`}
          />
          <StatCard
            label="Seuils atteints"
            value={String(monthStats.thresholds)}
            foot={
              monthStats.thresholdClients.length === 0
                ? 'aucun ce mois'
                : `${monthStats.thresholdClients.length > 1 ? 'concernés' : 'concerné'} : ${monthStats.thresholdClients.join(', ')}`
            }
          />
        </Row4>
      ) : null}

      <Split21 className="items-start">
        <Card>
          <CardBar
            start={
              <TableTabs
                aria-label="Filtrer par statut d'envoi"
                value={filter}
                onValueChange={(value) => {
                  setFilter(value);
                  pagination.setPage(1);
                }}
                items={tabs}
              />
            }
          />
          {alerts.isLoading ? (
            <LoadingState />
          ) : rows.length === 0 ? (
            <EmptyState title={empty.title} description={empty.description} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead kind="num">Seuil</TableHead>
                  <TableHead>Mois</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Envoyée le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((alert) => {
                  const status = statusView(alert.status);
                  return (
                    <TableRow key={alert.id}>
                      <TableCell kind="mono">{formatDateTime(alert.createdAt)}</TableCell>
                      <TableCell kind="name">
                        <Cell2
                          primary={clientLabel(alert)}
                          secondary={`Portée : ${scopeLabel(alert.scope).toLowerCase()}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Cell2
                          primary={alertTypeLabel(alert.type)}
                          secondary={alert.type}
                          secondaryMono
                        />
                      </TableCell>
                      <TableCell kind="num">
                        {alert.threshold !== null ? `${alert.threshold}\u00a0%` : '—'}
                      </TableCell>
                      <TableCell kind="mono">{alert.month}</TableCell>
                      <TableCell kind="sub" className="min-w-[200px] whitespace-normal">
                        {alert.message}
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell kind="mono">{formatDateTime(alert.sentAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!alerts.isLoading && rows.length > 0 ? (
            <Pager itemLabel="alertes" {...pagination.pagerProps} />
          ) : null}
        </Card>

        <Card>
          <CardHead
            title="Consommation et seuils"
            description="Consommation du mois (HT) par rapport au plafond de sécurité, repère au premier seuil d'alerte"
          />
          <CardContent className="grid gap-4">
            {clients.isLoading ? (
              <LoadingState className="py-6" />
            ) : clients.isError ? (
              <p className="m-0 text-small text-muted-foreground">
                Impossible de charger les clients
              </p>
            ) : thresholdLines.length === 0 ? (
              <p className="m-0 text-small text-muted-foreground">Aucun client.</p>
            ) : (
              thresholdLines.map(({ client, usage }) => (
                <ThresholdRow key={client.id} client={client} usage={usage} />
              ))
            )}
          </CardContent>
        </Card>
      </Split21>
    </>
  );
}
